// apps/web/src/routes/change-password.tsx
// Change Password Page - Simple design matching login page

import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCurrentUser, useChangePassword, useLogout } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Eye, EyeOff, Loader2, KeyRound, CheckCircle2 } from 'lucide-react'

export const Route = createFileRoute('/change-password')({
  component: ChangePasswordPage,
})

function ChangePasswordPage() {
  const navigate = useNavigate()
  const { data: user, isPending: isLoadingUser } = useCurrentUser()
  const changePasswordMutation = useChangePassword()
  const logoutMutation = useLogout()

  const [currentPassword, setCurrentPassword] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false)
  const [showNewPassword, setShowNewPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isLoadingUser && !user) {
      navigate({ to: '/login' })
    }
  }, [user, isLoadingUser, navigate])

  // Password validation
  const passwordChecks = React.useMemo(() => {
    return {
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      passwordsMatch: newPassword === confirmPassword && confirmPassword.length > 0,
    }
  }, [newPassword, confirmPassword])

  const isValidPassword =
    passwordChecks.minLength &&
    passwordChecks.hasUppercase &&
    passwordChecks.hasNumber &&
    passwordChecks.passwordsMatch

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValidPassword) return

    changePasswordMutation.mutate({
      currentPassword,
      newPassword,
      confirmPassword,
    })
  }

  const handleSkip = () => {
    if (!user?.mustChangePassword) {
      navigate({ to: '/' })
    }
  }

  // Show loading while checking auth
  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-svh bg-page">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-page px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo and Title */}
        <div className="flex flex-col items-center space-y-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <KeyRound className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-semibold text-text-dark">NurinV2</h1>
        </div>

        {/* Change Password Card */}
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-lg">Change Password</CardTitle>
            <CardDescription>
              {user?.mustChangePassword
                ? 'You must change your password before continuing'
                : 'Update your password to keep your account secure'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {changePasswordMutation.isError && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  {changePasswordMutation.error?.message || 'Failed to change password'}
                </div>
              )}

              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>
                {/* Password Requirements - inline */}
                {newPassword && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    <PasswordCheck passed={passwordChecks.minLength} label="8+ chars" />
                    <PasswordCheck passed={passwordChecks.hasUppercase} label="Uppercase" />
                    <PasswordCheck passed={passwordChecks.hasNumber} label="Number" />
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>
                {confirmPassword && !passwordChecks.passwordsMatch && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={changePasswordMutation.isPending || !isValidPassword || !currentPassword}
              >
                {changePasswordMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4" />
                )}
                Change Password
              </Button>

              {/* Skip Button - only if not forced */}
              {!user?.mustChangePassword && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleSkip}
                >
                  Skip for now
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Sign out link */}
        <p className="text-center text-xs text-muted-foreground">
          <button
            onClick={() => logoutMutation.mutate()}
            className="hover:text-foreground transition-colors"
          >
            Sign out and use different account
          </button>
        </p>
      </div>
    </div>
  )
}

// Password check item component
function PasswordCheck({ passed, label }: { passed: boolean; label: string }) {
  return (
    <span className={`flex items-center gap-1 ${passed ? 'text-success' : 'text-muted-foreground'}`}>
      {passed ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-3 w-3 rounded-full border" />}
      {label}
    </span>
  )
}
